import { fetcherConfig } from '../../../lib/api-fetcher';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { z } from 'zod';
import { PageHeader } from '../../../components/layout/PageHeader';
import { FormField, Input } from '../../../components/ui/FormField';
import { createAuthResource } from '@repo/api-client';
import { useToast } from '../../../components/ui/Toast';

const authResource = createAuthResource(fetcherConfig);

const passwordSettingsFormConfig = defineFormPage({
  resource: 'settings-password',
  defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  schema: z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Minimum 8 characters'),
    confirmPassword: z.string(),
  }),
});

export const Route = createFileRoute('/_auth/settings/password')({
  component: PasswordSettingsPage,
});

function PasswordSettingsPage() {
  usePageTitle('Password Settings');
  const { toast } = useToast();

  const { form, isSubmitting, fieldError: fe } = useFormPage(
    passwordSettingsFormConfig,
    { mutateAsync: async (v) => v },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
    if (!form.state.canSubmit) return;
    try {
      await authResource.forceChange({
        currentPassword: form.getFieldValue('currentPassword'),
        newPassword: form.getFieldValue('newPassword'),
      });
      toast({ type: 'success', title: 'Password updated' });
      form.reset();
    } catch (err: unknown) {
      console.error('Failed to update password:', err);
      const msg = err instanceof Error ? err.message : 'Failed to update password';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  return (
    <div className="max-w-md">
      <PageHeader title="Change password" />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <form.Field
          name="currentPassword"
          validators={{ onChange: z.string().min(1, 'Current password is required') }}
        >
          {(field: any) => {
            const error = fe(field.state.meta);
            return (
              <FormField label="Current password" required error={error} htmlFor="current-pw">
                <Input
                  id="current-pw"
                  type="password"
                  autoComplete="current-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isSubmitting}
                  error={!!error}
                />
              </FormField>
            );
          }}
        </form.Field>

        <form.Field
          name="newPassword"
          validators={{ onChange: z.string().min(8, 'Minimum 8 characters') }}
        >
          {(field: any) => {
            const error = fe(field.state.meta);
            return (
              <FormField
                label="New password"
                required
                error={error}
                hint="Minimum 8 characters, must include a letter and a number"
                htmlFor="new-pw"
              >
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isSubmitting}
                  error={!!error}
                />
              </FormField>
            );
          }}
        </form.Field>

        <form.Field
          name="confirmPassword"
          validators={{
            onChange: ({ value, fieldApi }: { value: any; fieldApi: any }) => {
              const newPw = fieldApi.form.getFieldValue('newPassword');
              if (value && value !== newPw) return 'Passwords do not match';
              return undefined;
            },
          }}
        >
          {(field: any) => {
            const error = fe(field.state.meta);
            return (
              <FormField label="Confirm new password" required error={error} htmlFor="confirm-pw">
                <Input
                  id="confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isSubmitting}
                  error={!!error}
                />
              </FormField>
            );
          }}
        </form.Field>

        <FormActions isSubmitting={isSubmitting} submitLabel="Update password" showCancel={false} />
      </form>
    </div>
  );
}